import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { Canvases } from '@air/api';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { CANVASES_LIST } from '~/swr-hooks/canvases/useListCanvases';

export const useRecordCanvasView = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: ({ canvasId }: { canvasId: string }) => {
      if (!currentWorkspace?.id) {
        return Promise.reject(new Error('No workspace'));
      }

      return callAirApi(() => Canvases.view({ workspaceId: currentWorkspace.id, canvasId }));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CANVASES_LIST] });
    },
    onError: (error, { canvasId }) => {
      reportErrorToBugsnag({
        error,
        context: 'Failed to record canvas view',
        metadata: { data: { canvasId, workspaceId: currentWorkspace?.id } },
      });
    },
  });

  const recordView = useCallback(
    (canvasId: string) => {
      if (!currentWorkspace?.id) {
        return;
      }
      mutate({ canvasId });
    },
    [currentWorkspace?.id, mutate],
  );

  return { recordView };
};
